/**
 * Keep `--app-height` in sync with the *visible* Mini App sheet.
 *
 * Telegram's `--tg-viewport-stable-height` does not update while the user
 * pulls the compact (~70%) sheet open (and on iOS often never updates after
 * the gesture). A CSS-only layout then stays short and the footer sits in
 * the middle / off-screen. We write the live height onto the document on
 * every viewport event.
 */
export function syncTelegramViewportHeight(): void {
  const webApp = window.Telegram?.WebApp;
  const visual = window.visualViewport;
  // Prefer live Telegram height (tracks the sheet as it is pulled), then
  // visualViewport, then innerHeight. Do not take Math.max with stable /
  // innerHeight — those stay at the compact or full-screen size and fight
  // the live value.
  const height = Math.round(
    webApp?.viewportHeight ||
      visual?.height ||
      window.innerHeight ||
      webApp?.viewportStableHeight ||
      0
  );
  if (height <= 0) return;

  const root = document.documentElement;
  root.style.setProperty("--app-height", `${height}px`);
  root.style.height = `${height}px`;
  document.body.style.height = `${height}px`;
  const appRoot = document.getElementById("root");
  if (appRoot) {
    appRoot.style.height = `${height}px`;
  }

  const safeBottom = Math.max(
    webApp?.safeAreaInset?.bottom ?? 0,
    webApp?.contentSafeAreaInset?.bottom ?? 0
  );
  root.style.setProperty("--app-safe-bottom", `${safeBottom}px`);
}

export function subscribeTelegramViewport(onChange?: () => void): () => void {
  let pollId: ReturnType<typeof setInterval> | undefined;

  const handle = (...args: unknown[]) => {
    syncTelegramViewportHeight();
    onChange?.();
    requestAnimationFrame(() => {
      syncTelegramViewportHeight();
      onChange?.();
    });

    const event = args[0] as { isStateStable?: boolean } | undefined;
    if (event?.isStateStable === false) {
      if (pollId !== undefined) clearInterval(pollId);
      let ticks = 0;
      pollId = setInterval(() => {
        syncTelegramViewportHeight();
        onChange?.();
        ticks += 1;
        if (ticks > 40) {
          if (pollId !== undefined) clearInterval(pollId);
          pollId = undefined;
        }
      }, 50);
    } else if (event?.isStateStable === true && pollId !== undefined) {
      clearInterval(pollId);
      pollId = undefined;
    }
  };

  handle();

  const webApp = window.Telegram?.WebApp;
  webApp?.onEvent?.("viewportChanged", handle);
  webApp?.onEvent?.("safeAreaChanged", handle);
  webApp?.onEvent?.("contentSafeAreaChanged", handle);

  window.addEventListener("resize", handle);
  window.visualViewport?.addEventListener("resize", handle);
  window.visualViewport?.addEventListener("scroll", handle);

  return () => {
    if (pollId !== undefined) clearInterval(pollId);
    webApp?.offEvent?.("viewportChanged", handle);
    webApp?.offEvent?.("safeAreaChanged", handle);
    webApp?.offEvent?.("contentSafeAreaChanged", handle);
    window.removeEventListener("resize", handle);
    window.visualViewport?.removeEventListener("resize", handle);
    window.visualViewport?.removeEventListener("scroll", handle);
  };
}
